import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';

type SliderArrowsOptions = {
  initialIsBeginning?: boolean;
  initialIsEnd?: boolean;
};

type SliderArrowsHandlers = {
  navigation: {
    prevEl: HTMLButtonElement | null;
    nextEl: HTMLButtonElement | null;
  };
  onSwiper: (swiper: SwiperInstance) => void;
  onSlideChange: (swiper: SwiperInstance) => void;
  onBeforeInit: (swiper: SwiperInstance) => void;
};

type SliderArrowsState = {
  prevButtonRef: RefObject<HTMLButtonElement | null>;
  nextButtonRef: RefObject<HTMLButtonElement | null>;
  isBeginning: boolean;
  isEnd: boolean;
};

type SliderArrowsProps = SliderArrowsState & {
  containerClassName?: string;
  buttonClassName?: string;
  leftButtonClassName?: string;
  rightButtonClassName?: string;
};

export const useSliderArrows = (
  options: SliderArrowsOptions = {}
): SliderArrowsState & SliderArrowsHandlers => {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const prevButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isBeginning, setIsBeginning] = useState(options.initialIsBeginning ?? true);
  const [isEnd, setIsEnd] = useState(options.initialIsEnd ?? false);

  const syncEdges = useCallback((swiper: SwiperInstance) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const onSwiper = useCallback(
    (swiper: SwiperInstance) => {
      swiperRef.current = swiper;
      syncEdges(swiper);
    },
    [syncEdges]
  );

  const onSlideChange = useCallback(
    (swiper: SwiperInstance) => {
      syncEdges(swiper);
    },
    [syncEdges]
  );

  const onBeforeInit = useCallback((swiper: SwiperInstance) => {
    if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
      swiper.params.navigation.prevEl = prevButtonRef.current;
      swiper.params.navigation.nextEl = nextButtonRef.current;
    }
  }, []);

  useEffect(() => {
    const swiper = swiperRef.current;
    const prevEl = prevButtonRef.current;
    const nextEl = nextButtonRef.current;

    if (!swiper || !prevEl || !nextEl) {
      return;
    }

    if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
      swiper.params.navigation.prevEl = prevEl;
      swiper.params.navigation.nextEl = nextEl;
      swiper.navigation.destroy();
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, []);

  return {
    prevButtonRef,
    nextButtonRef,
    isBeginning,
    isEnd,
    navigation: {
      prevEl: prevButtonRef.current,
      nextEl: nextButtonRef.current,
    },
    onSwiper,
    onSlideChange,
    onBeforeInit,
  };
};

export const SliderArrows = ({
  prevButtonRef,
  nextButtonRef,
  isBeginning,
  isEnd,
  containerClassName,
  buttonClassName,
  leftButtonClassName,
  rightButtonClassName,
}: SliderArrowsProps) => {
  const baseButtonClassName =
    'absolute top-1/2 z-10 flex size-[32px] -translate-y-1/2 items-center justify-center rounded-full bg-widget-gray-100 text-widget-text-primary shadow-sm transition-opacity';

  return (
    <div
      className={cn(
        'pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100',
        containerClassName
      )}
    >
      <button
        ref={prevButtonRef}
        type="button"
        aria-label="Previous slide"
        className={cn(
          baseButtonClassName,
          'left-[8px]',
          isBeginning ? 'opacity-50' : 'opacity-100',
          buttonClassName,
          leftButtonClassName
        )}
      >
        <ChevronLeft className="size-[18px]" />
      </button>
      <button
        ref={nextButtonRef}
        type="button"
        aria-label="Next slide"
        className={cn(
          baseButtonClassName,
          'right-[8px]',
          isEnd ? 'opacity-50' : 'opacity-100',
          buttonClassName,
          rightButtonClassName
        )}
      >
        <ChevronRight className="size-[18px]" />
      </button>
    </div>
  );
};
